/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { BasicFunction } from '../../../../helpers/jsHelpers/types';

import Localizer from '../../../../helpers/localization';
import { Setting } from '../../../../helpers/settings/types';
import { ExtendedItemType } from '../../../../helpers/ui/item/types';
import { convertToHumanReadable, isHearingImpairedSubtitlesTrack, isVisuallyImpairedAudioTrack } from '../../../../helpers/ui/language';
import { updateSetting } from '../../../../redux/ui/actions';
import { PictoArrowLeft, PictoCheck, PictoHearingImpaired, PictoLeaf } from '../../../pictos/Element';
import { type VideoPlayerMediaInfo } from '../../implementation/types';
import './Options.css';

enum OptionsType {
  Audio,
  Subtitles,
}

type OPTIONS_AND_SELECTION_TYPE = {
  optionsElement: React.Node,
  selectionElement: React.Node,
};

type OptionsPropType = {
  +audioMediaInfo: Array<VideoPlayerMediaInfo>,
  +itemType: ExtendedItemType,
  +onMouseEnter: BasicFunction,
  +onMouseLeave: BasicFunction,
  +onNewAudioTrackSelected: (index: number) => void,
  +onNewSubtitlesTrackSelected: (index: number) => void,
  +selectedAudioMediaInfo: number,
  +selectedSubtitlesMediaInfo: number,
  +subtitlesMediaInfo: Array<VideoPlayerMediaInfo>,
};

// eslint-disable-next-line max-lines-per-function
const Options = ({
  audioMediaInfo,
  itemType,
  onMouseEnter,
  onMouseLeave,
  onNewAudioTrackSelected,
  onNewSubtitlesTrackSelected,
  selectedAudioMediaInfo,
  selectedSubtitlesMediaInfo,
  subtitlesMediaInfo,
}: OptionsPropType): React.Node => {
  const dispatch = useDispatch();

  const isBitrateLimited = useSelector<boolean>((state) => state.ui.settings[Setting.GreenStreaming]);
  const isMaxBitrateAllowed = useSelector<boolean>((state) => state.appConfiguration.isMaxBitrateAllowed);

  const [displayedOptions, setDisplayedOptions] = useState<OptionsType | null>(null);

  const firstMainButtonRef = useRef<HTMLElement | null>(null);
  const firstAudioOptionRef = useRef<HTMLElement | null>(null);
  const firstSubtitleOptionRef = useRef<HTMLElement | null>(null);

  // Focus the first selection button when Options component mounts
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (firstMainButtonRef.current) {
        firstMainButtonRef.current.focus();
      }
    }, 0);

    return () => clearTimeout(focusTimer);
  }, []);

  // Shift focus into sub-menu when opened
  useEffect(() => {
    if (displayedOptions === OptionsType.Audio && firstAudioOptionRef.current) {
      firstAudioOptionRef.current.focus();
    } else if (displayedOptions === OptionsType.Subtitles && firstSubtitleOptionRef.current) {
      firstSubtitleOptionRef.current.focus();
    }
  }, [displayedOptions]);

  const toggleOptions = useCallback((optionsType: OptionsType) => {
    setDisplayedOptions((currentDisplayedOptions) => (optionsType === currentDisplayedOptions ? null : optionsType));
  }, []);

  const handleAudioOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      toggleOptions(OptionsType.Audio);
    },
    [toggleOptions],
  );

  const handleSubtitlesOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      toggleOptions(OptionsType.Subtitles);
    },
    [toggleOptions],
  );

  const handleNewOptionSelected = useCallback((event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, newOptionSelected: (index: number) => void, currentSelection: number) => {
    const { currentTarget } = event;

    // Get track index from HTML element or from its parent
    let id = currentTarget.getAttribute('data-id');
    if (id === null || id === '') {
      const { parentElement } = currentTarget;
      if (parentElement) {
        id = parentElement.getAttribute('data-id');
      }
    }

    // Empty string is magically converted to 0
    if (id !== null && id !== '' && !isNaN(id)) {
      const idNumber = Number(id);
      if (idNumber !== currentSelection) {
        newOptionSelected(idNumber);
      }
    }
  }, []);

  const handleNewAudioTrackOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      handleNewOptionSelected(event, onNewAudioTrackSelected, selectedAudioMediaInfo);
    },
    [handleNewOptionSelected, onNewAudioTrackSelected, selectedAudioMediaInfo],
  );

  const handleNewSubtitlesTrackOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      handleNewOptionSelected(event, onNewSubtitlesTrackSelected, selectedSubtitlesMediaInfo);
    },
    [handleNewOptionSelected, onNewSubtitlesTrackSelected, selectedSubtitlesMediaInfo],
  );

  const getCurrentAudio = useCallback((): { currentAudio: string | null, isVisuallyImpaired: boolean } => {
    const currentAudioMediaInfo = audioMediaInfo.find((elt) => elt.index === selectedAudioMediaInfo) ?? null;

    return {
      currentAudio: currentAudioMediaInfo ? convertToHumanReadable(currentAudioMediaInfo.lang) : null,
      isVisuallyImpaired: isVisuallyImpairedAudioTrack(currentAudioMediaInfo),
    };
  }, [audioMediaInfo, selectedAudioMediaInfo]);

  const getCurrentSubtitles = useCallback((): { currentSubtitles: string | null, isHearingImpaired: boolean } => {
    const currentSubtitlesMediaInfo = subtitlesMediaInfo.find((elt) => elt.index === selectedSubtitlesMediaInfo) ?? null;

    return {
      currentSubtitles: currentSubtitlesMediaInfo ? convertToHumanReadable(currentSubtitlesMediaInfo.lang) : null,
      isHearingImpaired: isHearingImpairedSubtitlesTrack(currentSubtitlesMediaInfo),
    };
  }, [subtitlesMediaInfo, selectedSubtitlesMediaInfo]);

  const buildAudioMediaInfoList = useCallback((): Array<React.Node> => {
    const audioMediaInfoList: Array<React.Node> = [];
    const isSubMenuVisible = displayedOptions === OptionsType.Audio;

    for (let i = 0; i < audioMediaInfo.length; i++) {
      const { [i]: audio } = audioMediaInfo;
      const { index, lang } = audio;
      const audioIndicator = index === selectedAudioMediaInfo ? <PictoCheck className='cell' /> : <div className='cell' />;
      const humanReadableLang = convertToHumanReadable(lang);

      audioMediaInfoList.push(
        <button
          aria-checked={index === selectedAudioMediaInfo}
          aria-label={humanReadableLang}
          className='option'
          data-id={index}
          key={index}
          onClick={handleNewAudioTrackOnClick}
          ref={i === 0 ? firstAudioOptionRef : undefined}
          role='menuitemradio'
          tabIndex={isSubMenuVisible ? 0 : -1}
          type='button'
        >
          {audioIndicator}
          <div className='cell optionLabel'>{humanReadableLang}</div>
        </button>,
      );
    }

    return audioMediaInfoList;
  }, [audioMediaInfo, displayedOptions, handleNewAudioTrackOnClick, selectedAudioMediaInfo]);

  const buildSubtitlesMediaInfoList = useCallback((): Array<React.Node> => {
    if (subtitlesMediaInfo.length === 0) {
      return [];
    }

    const isSubMenuVisible = displayedOptions === OptionsType.Subtitles;

    const subtitlesMediaInfoList: Array<React.Node> = [
      <button
        aria-checked={selectedSubtitlesMediaInfo < 0}
        aria-label={Localizer.localize('accessibility.player.options.disable_subtitles')}
        className='option'
        data-id={-1}
        key='-1'
        onClick={handleNewSubtitlesTrackOnClick}
        ref={firstSubtitleOptionRef}
        role='menuitemradio'
        tabIndex={isSubMenuVisible ? 0 : -1}
        type='button'
      >
        {selectedSubtitlesMediaInfo < 0 ? <PictoCheck className='cell' /> : <div className='cell' />}
        <div className='cell optionLabel'>{Localizer.localize('player.subtitles.none')}</div>
      </button>,
    ];

    for (let i = 0; i < subtitlesMediaInfo.length; ++i) {
      const { [i]: subtitles } = subtitlesMediaInfo;
      const { index, lang } = subtitles;
      const subtitlesIndicator = index === selectedSubtitlesMediaInfo ? <PictoCheck className='cell' /> : <div className='cell' />;

      const isHearingImpaired = isHearingImpairedSubtitlesTrack(subtitles);
      const hearingImpairedElt = isHearingImpaired ? <PictoHearingImpaired className='impaired' /> : null;
      const humanReadableLang = convertToHumanReadable(lang);

      subtitlesMediaInfoList.push(
        <button
          aria-checked={index === selectedSubtitlesMediaInfo}
          aria-label={`${humanReadableLang} ${isHearingImpaired ? Localizer.localize('accessibility.picto.hearing_impaired') : ''}`}
          className='option'
          data-id={index}
          key={index}
          onClick={handleNewSubtitlesTrackOnClick}
          role='menuitemradio'
          tabIndex={isSubMenuVisible ? 0 : -1}
          type='button'
        >
          {subtitlesIndicator}
          <div className='cell optionLabel'>
            {humanReadableLang}
            {hearingImpairedElt}
          </div>
        </button>,
      );
    }

    return subtitlesMediaInfoList;
  }, [displayedOptions, subtitlesMediaInfo, selectedSubtitlesMediaInfo, handleNewSubtitlesTrackOnClick]);

  const renderSelectionAndOptions = useCallback(
    (
      optionsType: OptionsType,
      optionsList: Array<React.Node>,
      label: string,
      selection: string | null,
      impairedPicto: React.Node,
      clickCallback: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
      isFirstSelection?: boolean,
    ): OPTIONS_AND_SELECTION_TYPE => {
      const isExpanded = optionsType === displayedOptions;
      const optionsElement = (
        <div className={clsx('optionsContainer', isExpanded && 'visible')} role='menu'>
          {optionsList}
        </div>
      );

      const selectionElement = (
        <button
          aria-expanded={isExpanded}
          aria-haspopup='true'
          aria-label={Localizer.localize(optionsType === OptionsType.Audio ? 'accessibility.player.options.change_audio_track' : 'accessibility.player.options.change_subtitles_track', {
            name: selection ?? '',
          })}
          className={clsx('selection', isExpanded && 'highlighted')}
          onClick={clickCallback}
          ref={isFirstSelection ? firstMainButtonRef : undefined}
          role='menuitem'
          type='button'
        >
          <PictoArrowLeft className='cell' forceHoverEffect />
          <div className='cell selectionTitle'>{label}</div>
          <div className='cell selectionInfo'>
            {selection}
            {impairedPicto}
          </div>
        </button>
      );

      return {
        optionsElement,
        selectionElement,
      };
    },
    [displayedOptions],
  );

  const renderAudio = useCallback((): OPTIONS_AND_SELECTION_TYPE => {
    const audioMediaInfoList = buildAudioMediaInfoList();

    if (audioMediaInfoList.length <= 1) {
      // 0 or 1 audio stream: no choice for the user
      return {
        optionsElement: null,
        selectionElement: null,
      };
    }

    const { currentAudio } = getCurrentAudio();

    return renderSelectionAndOptions(OptionsType.Audio, audioMediaInfoList, Localizer.localize('player.options.audio'), currentAudio, null, handleAudioOnClick, true);
  }, [buildAudioMediaInfoList, getCurrentAudio, renderSelectionAndOptions, handleAudioOnClick]);

  const renderSubtitles = useCallback((): OPTIONS_AND_SELECTION_TYPE => {
    const subtitlesMediaInfoList = buildSubtitlesMediaInfoList();

    if (subtitlesMediaInfoList.length === 0) {
      // 0 subtitles stream: no choice for the user
      return {
        optionsElement: null,
        selectionElement: null,
      };
    }

    const { currentSubtitles, isHearingImpaired } = getCurrentSubtitles();
    const hearingImpairedElt = isHearingImpaired ? <PictoHearingImpaired className='impaired' /> : null;
    const isFirstAvailable = audioMediaInfo.length <= 1;

    return renderSelectionAndOptions(
      OptionsType.Subtitles,
      subtitlesMediaInfoList,
      Localizer.localize('player.options.subtitles'),
      currentSubtitles ?? Localizer.localize('player.subtitles.none'),
      hearingImpairedElt,
      handleSubtitlesOnClick,
      isFirstAvailable,
    );
  }, [audioMediaInfo.length, buildSubtitlesMediaInfoList, getCurrentSubtitles, handleSubtitlesOnClick, renderSelectionAndOptions]);

  const handleLimitBitrateOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      dispatch(updateSetting(Setting.GreenStreaming, !isBitrateLimited));
    },
    [dispatch, isBitrateLimited],
  );

  const renderGreenStreamingOption = useCallback((): React.Node => {
    if (!isMaxBitrateAllowed || itemType === ExtendedItemType.OfflineContent) {
      return null;
    }

    const isFirstAvailable = audioMediaInfo.length <= 1 && subtitlesMediaInfo.length === 0;

    return (
      <button
        aria-checked={isBitrateLimited}
        aria-label={Localizer.localize('accessibility.player.options.green_streaming')}
        className='selection greenStreaming'
        onClick={handleLimitBitrateOnClick}
        ref={isFirstAvailable ? firstMainButtonRef : undefined}
        role='menuitemcheckbox'
        type='button'
      >
        {isBitrateLimited ? <PictoCheck className='cell' /> : <div className='cell' />}
        <div className='cell selectionTitle'>
          {Localizer.localize('settings.audio_video.green_streaming.label')}
          <PictoLeaf className='leaf' />
        </div>
      </button>
    );
  }, [audioMediaInfo.length, handleLimitBitrateOnClick, isBitrateLimited, isMaxBitrateAllowed, itemType, subtitlesMediaInfo.length]);

  const { optionsElement: audioOptions, selectionElement: audioSelection } = renderAudio();
  const { optionsElement: subtitlesOptions, selectionElement: subtitlesSelection } = renderSubtitles();

  return (
    <div aria-label={Localizer.localize('accessibility.player.options.menu')} className='options' id='options-menu' onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} role='menu'>
      {audioOptions}
      {subtitlesOptions}
      {audioSelection}
      {subtitlesSelection}
      {renderGreenStreamingOption()}
    </div>
  );
};

export default Options;
